import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDeleteFile, onGetForm, onGetOne, onSave } from 'redux/actions/divorces.actions';
import {
  divorceFormSelector,
  divorcesIdCardTypesSelector,
  divorcesFilesSelector,
} from 'redux/selectors/divorces.selector';
import { userSelector } from 'redux/selectors/auth.selector';
import { isClienteRole } from 'helpers/roles';
import { history } from 'helpers/history';

import LayoutContainer from 'containers/LayoutContainer';
import DivorcesForm from 'views/public/DivorcesView/components/DivorcesForm';
import Dialog from 'components/commons/Dialog';
import Loading from 'components/commons/Loading';

import { IOption } from 'types/common.types';
import { IDivorcesFormData } from 'views/public/DivorcesView/components/DivorcesForm/types';

import 'views/public/DivorcesView/components/DivorcesForm/i18n';

const DivorcesFormContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IOption | undefined>();
  const { id = '' } = useParams<{ id?: string }>();
  const initialValues = useTypedSelector(divorceFormSelector);
  const idCardTypes = useTypedSelector(divorcesIdCardTypesSelector);
  const { loading } = useTypedSelector((state) => state.divorces);
  const files = useTypedSelector(divorcesFilesSelector);
  const isEditing = !!id;

  useEffect(() => {
    dispatch(onGetForm());
    if (id) dispatch(onGetOne(id));
    return () => {
      dispatch(onGetOne(''));
    };
  }, [dispatch, id]);

  const onSubmit = (formData: IDivorcesFormData) => dispatch(onSave(formData, id));

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDeleteFile(id, `${deleteItem.data}`));
    setDeleteItem(undefined);
  };

  const user = useTypedSelector(userSelector);
  const item = useTypedSelector((state) => state.divorces.selectedItem);

  useEffect(() => {
    if (!loading && isEditing && item) {
      const isClient = isClienteRole(user);
      if (isClient && item.state !== 'OPENED') {
        history.push(`/public/procedures/forms/divorces/${id}/show`);
      }
    }
  }, [loading, isEditing, item, user, id]);

  const isClient = isClienteRole(user);
  const waitingForData = isEditing && !item;
  const isUnauthorized = isEditing && isClient && item && item.state !== 'OPENED';

  if (loading || waitingForData || isUnauthorized) {
    return (
      <LayoutContainer isPublic title={i18next.t<string>('PUBLIC_PROCEDURES_VIEW:TITLE')}>
        <Loading isModalMode message={i18next.t('DIVORCES_FORM:LOADING')} />
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer isPublic title={i18next.t<string>('PUBLIC_PROCEDURES_VIEW:TITLE')}>
      <DivorcesForm
        files={files}
        idCardTypes={idCardTypes}
        initialValues={initialValues}
        isEditing={isEditing}
        onDeleteFile={setDeleteItem}
        onSubmit={onSubmit}
      />
      <Dialog
        message={i18next.t<string>('DIVORCES_FORM:DELETE_DIALOG_MESSAGE', {
          name: deleteItem?.label,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t<string>('DIVORCES_FORM:DELETE_DIALOG_TITLE')}
      />
      {loading && <Loading isModalMode message={i18next.t('DIVORCES_FORM:LOADING')} />}
    </LayoutContainer>
  );
};

export default DivorcesFormContainer;
